<template>
  <div class="drawer-overlay" v-if="visible" @click.self="close">
    <div class="drawer-panel" :class="{ open: visible }">
      <div class="drawer-header">
        <div class="drawer-user" v-if="isLoggedIn">
          <div class="drawer-profile-main">
            <div class="drawer-avatar-wrap">
              <div class="drawer-avatar">
                <img :src="avatarUrl || defaultAvatarUrl" :alt="$t('用户头像')">
              </div>
              <div class="avatar-badge"></div>
            </div>
            <div class="drawer-profile-info">
              <div class="drawer-address">{{ user.address }}</div>
              <div class="drawer-level-row">
                <span>{{ localizedMemberLevel(user && user.member_level_name) }}</span>
                <em>{{ memberRateText }}</em>
              </div>
            </div>
          </div>
          <div class="drawer-stats">
            <div class="stat-box">
              <div class="stat-label">{{ $t('钱包余额') }}</div>
              <div class="stat-val balance-color">{{ formatBalance(balance) }}</div>
              <div class="stat-sub">USDT</div>
            </div>
            <div class="stat-divider"></div>
            <div class="stat-box">
              <div class="stat-label">{{ $t('收益余额') }}</div>
              <div class="stat-val earn-color">{{ formatBalance(availableEarnings) }}</div>
              <div class="stat-sub">USDT</div>
            </div>
          </div>
        </div>
        <div class="drawer-user drawer-guest" v-else>
          <div class="drawer-avatar-wrap">
            <div class="drawer-avatar guest-avatar"><img :src="defaultAvatarUrl" :alt="$t('用户头像')"></div>
          </div>
          <div class="drawer-address">{{ $t('未登录') }}</div>
          <div class="guest-tip">{{ $t('连接钱包以使用全部功能') }}</div>
        </div>
        <button class="drawer-close" @click="close" aria-label="关闭">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
        </button>
      </div>

      <div class="drawer-body" v-if="isLoggedIn">
        <div class="personal-earning-preview">
          <div class="personal-earning-head">
            <div>
              <span>{{ $t('收益预估') }}</span>
              <strong>{{ $t('正式产品每日收益') }}</strong>
            </div>
            <em v-if="officialEarningPreview && Number(officialEarningPreview.active_order_count) > 0">
              {{ formatPercent(officialEarningPreview.daily_rate) }}
            </em>
          </div>

          <template v-if="officialEarningPreview && Number(officialEarningPreview.active_order_count) > 0">
            <div class="personal-earning-formula">
              <div>
                <span>{{ $t('HL交易区产品金额') }}</span>
                <strong>{{ formatBalance(officialEarningPreview.official_amount) }} U</strong>
              </div>
              <b>+</b>
              <div>
                <span>{{ $t('收益余额') }}</span>
                <strong>{{ formatBalance(earningsBalanceFromPreview(officialEarningPreview)) }} U</strong>
              </div>
              <b>=</b>
              <div>
                <span>{{ $t('收益基数') }}</span>
                <strong>{{ formatBalance(officialEarningPreview.earning_base) }} U</strong>
              </div>
            </div>
            <div class="personal-earning-result">
              <span>{{ $t('预估每日收益') }}</span>
              <strong>+{{ formatBalance(officialEarningPreview.estimated_daily_earnings) }} U</strong>
            </div>
          </template>
          <div v-else class="personal-earning-empty">
            {{ $t('购买HL交易区产品后显示收益率') }}
          </div>
        </div>

        <div class="menu-group">
          <div class="menu-label">{{ $t('钱包管理') }}</div>
          <div class="menu-item" @click="goTo('recharge')">
            <div class="menu-icon-wrap green">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M12 2v20M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6"></path></svg>
            </div>
            <span class="menu-text">{{ $t('充值') }}</span>
            <svg class="menu-arrow" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="9 18 15 12 9 6"></polyline></svg>
          </div>
          <div class="menu-item" @click="goTo('withdraw')">
            <div class="menu-icon-wrap orange">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><line x1="12" y1="5" x2="12" y2="19"></line><polyline points="19 12 12 19 5 12"></polyline></svg>
            </div>
            <span class="menu-text">{{ $t('提现') }}</span>
            <svg class="menu-arrow" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="9 18 15 12 9 6"></polyline></svg>
          </div>
        </div>

        <div class="menu-group">
          <div class="menu-label">{{ $t('我的记录') }}</div>
          <div class="menu-item" @click="goTo('orders')">
            <div class="menu-icon-wrap blue">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M16 4h2a2 2 0 012 2v14a2 2 0 01-2 2H6a2 2 0 01-2-2V6a2 2 0 012-2h2"></path><rect x="8" y="2" width="8" height="4" rx="1"></rect></svg>
            </div>
            <span class="menu-text">{{ $t('购买记录') }}</span>
            <svg class="menu-arrow" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="9 18 15 12 9 6"></polyline></svg>
          </div>
          <div class="menu-item" @click="goTo('transactions')">
            <div class="menu-icon-wrap purple">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"></polyline></svg>
            </div>
            <span class="menu-text">{{ $t('交易记录') }}</span>
            <svg class="menu-arrow" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="9 18 15 12 9 6"></polyline></svg>
          </div>
          <div class="menu-item" @click="goTo('invite')">
            <div class="menu-icon-wrap teal">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M16 21v-2a4 4 0 00-4-4H6a4 4 0 00-4 4v2"></path><circle cx="9" cy="7" r="4"></circle><line x1="19" y1="8" x2="19" y2="14"></line><line x1="22" y1="11" x2="16" y2="11"></line></svg>
            </div>
            <span class="menu-text">{{ $t('邀请好友') }}</span>
            <svg class="menu-arrow" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="9 18 15 12 9 6"></polyline></svg>
          </div>
          <div class="menu-item" @click="goTo('team')">
            <div class="menu-icon-wrap indigo">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round">
                <circle cx="9" cy="7" r="4"></circle>
                <path d="M2 21v-2a4 4 0 014-4h6a4 4 0 014 4v2"></path>
                <path d="M16 3.5a4 4 0 010 7.5M18 15a4 4 0 014 4v2"></path>
              </svg>
            </div>
            <span class="menu-text">{{ $t('我的团队') }}</span>
            <svg class="menu-arrow" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="9 18 15 12 9 6"></polyline></svg>
          </div>
          <div class="menu-item" @click="goTo('project')">
            <div class="menu-icon-wrap green">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 21h18"></path><path d="M5 21V5l7-3 7 3v16"></path><path d="M9 9h6M9 13h6M9 17h6"></path></svg>
            </div>
            <span class="menu-text">{{ $t('项目介绍') }}</span>
            <svg class="menu-arrow" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="9 18 15 12 9 6"></polyline></svg>
          </div>
        </div>

        <div class="menu-group menu-group-last">
          <div class="menu-item menu-logout" @click="logout">
            <div class="menu-icon-wrap red">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4"></path><polyline points="16 17 21 12 16 7"></polyline><line x1="21" y1="12" x2="9" y2="12"></line></svg>
            </div>
            <span class="menu-text">{{ $t('退出登录') }}</span>
          </div>
        </div>
      </div>

      <div class="drawer-body drawer-empty-body" v-else>
        <div class="drawer-empty">
          <div class="empty-icon">--</div>
          <p>{{ $t('请先连接钱包登录') }}</p>
          <button class="btn btn-primary btn-block" @click="connectAndClose" style="margin-top:16px">
            {{ $t('连接钱包') }}
          </button>
        </div>
      </div>

      <transition name="slide">
        <div class="sub-panel" v-if="currentPanel">
          <div class="sub-header">
            <button class="sub-back" @click="backToMenu" aria-label="返回">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><polyline points="15 18 9 12 15 6"></polyline></svg>
            </button>
            <span class="sub-title">{{ panelTitle }}</span>
          </div>

          <div class="sub-content">
            <div v-if="currentPanel === 'recharge'" class="action-panel">
              <div class="wallet-hero">
                <span>{{ $t('当前余额') }}</span>
                <strong>{{ formatBalance(balance) }} USDT</strong>
              </div>

              <template v-if="walletPayEnabled">
                <template v-if="!paymentOrder">
                  <div class="section-title">{{ $t('自动充值') }}</div>
                  <div class="form-stack">
                    <label class="field-card">
                      <span class="field-label">{{ $t('充值金额') }}</span>
                      <div class="field-control">
                        <input type="number" v-model="rechargeForm.amount" min="0.01" step="0.01" inputmode="decimal" placeholder="0.00" />
                        <em>USDT</em>
                      </div>
                    </label>
                  </div>
                  <div class="form-tips">{{ $t('创建订单后会生成本次充值专用地址。支付平台确认到账后，余额将自动入账。') }}</div>
                  <div class="sticky-actions">
                    <button class="primary-action success-action" @click="createPaymentOrder" :disabled="creatingPayment">
                      {{ creatingPayment ? $t('订单创建中...') : $t('生成充值地址') }}
                    </button>
                  </div>
                </template>

                <template v-else>
                  <div class="payment-status-card">
                    <div class="payment-status-row">
                      <div>
                        <span>{{ $t('订单金额') }}</span>
                        <strong>{{ paymentOrder.amount || rechargeForm.amount }} USDT</strong>
                      </div>
                      <b :class="['payment-status', paymentStatusClass(paymentOrder.status)]">
                        {{ paymentStatusText(paymentOrder.status) }}
                      </b>
                    </div>
                    <div class="payment-meta" v-if="paymentOrder.network_name">
                      {{ $t('支付网络：') }}{{ paymentOrder.network_name }}
                    </div>
                    <div class="payment-meta" v-if="paymentOrder.expires_at">
                      {{ $t('有效期至：') }}{{ paymentOrder.expires_at }}
                    </div>
                  </div>

                  <div v-if="paymentOrder.wallet_address" class="recharge-address-card payment-address-card">
                    <div class="address-main">
                      <div>
                        <div class="address-network">{{ paymentOrder.network_name || $t('充值地址') }}</div>
                        <input
                          class="address-text address-text-input"
                          :value="paymentOrder.wallet_address"
                          readonly
                          @focus="selectAddress"
                        >
                      </div>
                      <button class="mini-action" @click="copyRechargeAddress(paymentOrder.wallet_address, $event)">{{ $t('复制') }}</button>
                    </div>
                    <div v-if="paymentOrder.qr_code" class="qr-wrap">
                      <img class="qr-image" :src="paymentOrder.qr_code" alt="充值二维码">
                    </div>
                  </div>

                  <a
                    v-if="paymentOrder.payment_url"
                    class="payment-link"
                    :href="paymentOrder.payment_url"
                    target="_blank"
                    rel="noopener noreferrer"
                  >{{ $t('打开支付页面') }}</a>

                  <div class="payment-order-no" v-if="paymentOrder.trade_no">
                    {{ $t('平台订单号：') }}{{ paymentOrder.trade_no }}
                  </div>
                  <div class="form-tips" v-if="paymentOrder.status !== 'paid'">
                    {{ $t('请严格按照订单金额和指定网络转账。完成后可点击“查询到账状态”。') }}
                  </div>
                  <div class="form-tips payment-paid-tip" v-else>
                    {{ $t('充值已到账，钱包余额已自动更新。') }}
                  </div>

                  <div class="sticky-actions payment-actions">
                    <button class="secondary-action" @click="resetPaymentOrder" :disabled="checkingPayment">{{ $t('新建订单') }}</button>
                    <button class="primary-action success-action" @click="checkPaymentOrder" :disabled="checkingPayment">
                      {{ checkingPayment ? $t('查询中...') : $t('查询到账状态') }}
                    </button>
                  </div>
                </template>
              </template>

              <template v-else-if="manualRechargeEnabled">
                <div class="section-title">{{ $t('收款地址') }}</div>
                <div v-if="rechargeConfigs.length" class="recharge-address-list">
                  <div class="recharge-address-card" v-for="item in rechargeConfigs" :key="item.id">
                    <div class="address-main">
                      <div>
                        <div class="address-network">{{ item.network_name }}</div>
                        <input
                          class="address-text address-text-input"
                          :value="item.wallet_address"
                          readonly
                          @focus="selectAddress"
                        >
                      </div>
                      <button class="mini-action" @click="copyRechargeAddress(item.wallet_address, $event)">{{ $t('复制') }}</button>
                    </div>
                    <div v-if="item.qr_code" class="qr-wrap">
                      <img class="qr-image" :src="assetUrl(item.qr_code)" alt="充值二维码">
                    </div>
                  </div>
                </div>
                <div v-else class="config-empty">{{ $t('暂无可用充值地址，请联系客服或稍后再试') }}</div>

                <div class="form-stack">
                  <label class="field-card">
                    <span class="field-label">{{ $t('充值金额') }}</span>
                    <div class="field-control">
                      <input type="number" v-model="rechargeForm.amount" placeholder="0.00" />
                      <em>USDT</em>
                    </div>
                  </label>

                  <label class="upload-card" :class="{ uploaded: rechargeForm.screenshot }">
                    <input type="file" accept="image/*" @change="handleProofChange" />
                    <span class="upload-title">{{ rechargeForm.screenshot ? $t('凭证已上传') : $t('上传充值凭证') }}</span>
                    <span class="upload-desc">{{ uploadingProof ? $t('上传中，请稍候') : $t('支持截图或转账回执图片') }}</span>
                    <img v-if="rechargeForm.screenshot" class="proof-preview" :src="assetUrl(rechargeForm.screenshot)" alt="充值凭证">
                  </label>
                </div>

                <div class="form-tips">{{ $t('转账完成后提交金额和凭证，后台审核通过后余额会自动入账。') }}</div>

                <div class="sticky-actions">
                  <button class="primary-action success-action" @click="submitRecharge" :disabled="submitting || uploadingProof">
                    {{ submitting ? $t('提交中...') : uploadingProof ? $t('上传中...') : $t('提交充值申请') }}
                  </button>
                </div>
              </template>

              <template v-else>
                <div class="section-title">{{ $t('充值暂未开放') }}</div>
                <div class="config-empty">{{ $t('当前充值通道暂未开放，请稍后再试') }}</div>
              </template>
            </div>

            <div v-if="currentPanel === 'withdraw'" class="action-panel">
              <div class="wallet-hero withdraw-hero">
                <span>{{ $t('可提现收益') }}</span>
                <strong>{{ formatBalance(availableEarnings) }} USDT</strong>
              </div>

              <div class="form-stack">
                <label class="field-card">
                  <span class="field-label">{{ $t('提现金额') }}</span>
                  <div class="field-control">
                    <input type="number" v-model="withdrawForm.amount" placeholder="0.00" />
                    <em>USDT</em>
                  </div>
                </label>

                <label class="field-card">
                  <span class="field-label">{{ $t('提现地址') }}</span>
                  <div class="field-control address-input">
                    <textarea v-model="withdrawForm.to_address" rows="2" spellcheck="false" :placeholder="$t('请输入提现钱包地址')"></textarea>
                  </div>
                </label>
              </div>

              <div class="withdraw-summary">
                <div>
                  <span>{{ $t('提现手续费（') }}{{ formatPercent(withdrawConfig.fee_percent) }}{{ $t('）') }}</span>
                  <strong>-{{ formatBalance(withdrawFeeAmount) }} USDT</strong>
                </div>
                <div class="withdraw-arrival">
                  <span>{{ $t('预计到账') }}</span>
                  <strong>{{ formatBalance(withdrawActualAmount) }} USDT</strong>
                </div>
              </div>

              <div class="form-tips">
                <span>{{ $t('仅收益金额可提现，钱包充值余额不可提现。') }}</span><br>
                <template v-if="Number(withdrawConfig.min_amount) > 0">
                  {{ $t('最低提现 ') }}{{ formatBalance(withdrawConfig.min_amount) }} USDT{{ $t('。') }}
                </template>
                {{ localizedWithdrawContent }}
                <span v-if="Number(withdrawConfig.withdraw_time_enabled) === 1">{{ $t('提现时间：') }}{{ withdrawConfig.withdraw_start_time || '00:00' }}-{{ withdrawConfig.withdraw_end_time || '23:59' }}</span>
              </div>

              <div class="sticky-actions">
                <button class="primary-action" @click="submitWithdraw" :disabled="submitting">
                  {{ submitting ? $t('提交中...') : $t('提交提现申请') }}
                </button>
              </div>

              <div class="withdraw-history" v-if="withdrawList.length">
                <div class="section-title">{{ $t('提现记录') }}</div>
                <div class="withdraw-history-item" v-for="item in withdrawList" :key="item.id">
                  <div>
                    <strong>{{ formatBalance(item.amount) }} USDT</strong>
                    <span>{{ formatChinaTime(item.created_at) }}</span>
                  </div>
                  <em :class="'withdraw-status-' + item.status">{{ withdrawStatusText(item.status) }}</em>
                </div>
              </div>
            </div>

            <div v-if="currentPanel === 'project'" class="project-panel">
              <div class="project-cover">
                <span>PROJECT</span>
                <strong>{{ localizedProjectTitle }}</strong>
              </div>
              <article class="project-copy" v-if="localizedProjectContent" v-html="localizedProjectContent"></article>
              <div class="config-empty" v-else>{{ $t('项目介绍暂未配置') }}</div>
            </div>

            <div v-if="currentPanel === 'orders'" class="list-panel modern-list">
              <div class="segmented-tabs">
                <button :class="{ active: orderTab === 'trial' }" @click="orderTab = 'trial'">{{ $t('HL体验区') }}</button>
                <button :class="{ active: orderTab === 'official' }" @click="orderTab = 'official'">{{ $t('HL交易区') }}</button>
              </div>

              <div class="record-list" v-if="orderList.length > 0">
                <div class="record-card" v-for="item in orderList" :key="item.id">
                  <div class="record-header">
                    <span class="record-name">{{ localizedOrderProductName(item) }}</span>
                    <span class="record-amount neg">-{{ formatBalance(item.amount) }}</span>
                  </div>
                  <div class="record-grid">
                    <div class="record-info">
                      <span>{{ $t('日收益率') }}</span>
                      <strong class="green">{{ formatPercent(orderTab === 'official' ? (item.effective_daily_rate || item.daily_rate) : item.daily_rate) }}</strong>
                    </div>
                    <div class="record-info" v-if="orderTab === 'trial'">
                      <span>{{ $t('收益进度') }}</span>
                      <strong>{{ item.earned_days }}/{{ item.period_days }}{{ $t('天') }}</strong>
                    </div>
                    <div class="record-info" v-else>
                      <span>{{ $t('状态') }}</span>
                      <strong class="green">{{ $t('持续收益中') }}</strong>
                    </div>
                  </div>
                  <div class="record-footer">
                    <span class="record-date">{{ formatChinaTime(item.purchased_at) }}</span>
                    <span class="record-tag" :class="'tag-' + item.status">{{ orderStatusText(item) }}</span>
                  </div>
                </div>
              </div>
              <div class="empty-panel" v-else>
                <div class="empty-title">{{ $t('暂无购买记录') }}</div>
                <div class="empty-desc">{{ $t('购买产品后会在这里展示收益进度。') }}</div>
              </div>
              <button class="load-more" v-if="hasMoreOrders" @click="loadMoreOrders">{{ $t('加载更多') }}</button>
            </div>

            <div v-if="currentPanel === 'transactions'" class="list-panel modern-list">
              <div class="transaction-filters">
                <select v-model="txFilters.type">
                  <option value="">{{ $t('全部类型') }}</option>
                  <option value="recharge">{{ $t('充值') }}</option>
                  <option value="withdraw">{{ $t('提现') }}</option>
                  <option value="buy_trial">{{ $t('购买HL体验区') }}</option>
                  <option value="buy_official">{{ $t('购买HL交易区') }}</option>
                  <option value="earning">{{ $t('收益入账') }}</option>
                  <option value="referral_reward">{{ $t('直推奖励') }}</option>
                  <option value="level_reward">{{ $t('等级奖励') }}</option>
                </select>
                <div class="transaction-date-range">
                  <label>
                    <span>{{ $t('开始日期') }}</span>
                    <DatePicker v-model="txFilters.start_date" />
                  </label>
                  <label>
                    <span>{{ $t('结束日期') }}</span>
                    <DatePicker v-model="txFilters.end_date" />
                  </label>
                </div>
                <div class="transaction-filter-actions">
                  <button class="filter-reset" @click="resetTxFilters">{{ $t('重置') }}</button>
                  <button class="filter-submit" @click="applyTxFilters">{{ $t('筛选') }}</button>
                </div>
              </div>

              <div class="record-list" v-if="txList.length > 0">
                <div class="record-card" v-for="item in txList" :key="item.id">
                  <div class="record-header">
                    <span class="record-type-badge" :class="'type-' + item.type">{{ txTypeLabel(item.type) }}</span>
                    <span class="record-amount" :class="{ positive: Number(item.amount) > 0 }">
                      {{ Number(item.amount) > 0 ? '+' : '' }}{{ formatBalance(item.amount) }}
                    </span>
                  </div>
                  <div class="record-grid">
                    <div class="record-info">
                      <span>{{ $t('备注') }}</span>
                      <strong>{{ txRemark(item.remark) }}</strong>
                    </div>
                    <div class="record-info">
                      <span>{{ $t('余额') }}</span>
                      <strong>{{ formatBalance(item.balance_after) }}</strong>
                    </div>
                  </div>
                  <div class="record-footer">
                    <span class="record-date">{{ formatChinaTime(item.created_at) }}</span>
                  </div>
                </div>
              </div>
              <div class="empty-panel" v-else>
                <div class="empty-title">{{ $t('暂无交易记录') }}</div>
                <div class="empty-desc">{{ $t('充值、提现和收益流水会在这里汇总。') }}</div>
              </div>
              <button class="load-more" v-if="hasMoreTx" @click="loadMoreTx">{{ $t('加载更多') }}</button>
            </div>

            <div v-if="currentPanel === 'invite'" class="invite-panel">
              <div class="invite-hero">
                <span>{{ $t('专属用户ID') }}</span>
                <strong>{{ inviteCode || '------' }}</strong>
                <p>{{ $t('好友首次钱包登录后自动绑定。HL体验区产品不返佣，HL交易区产品按单笔订单金额匹配最高返佣档位。') }}</p>
              </div>

              <div class="invite-stats invite-stats-wide">
                <div>
                  <span>{{ $t('已邀请') }}</span>
                  <strong>{{ inviteInfo.invited_count || 0 }}</strong>
                </div>
                <div>
                  <span>{{ $t('直推奖励') }}</span>
                  <strong>{{ formatBalance(inviteInfo.total_direct_reward) }}</strong>
                  <small>USDT</small>
                </div>
              </div>

              <div class="reward-rules">
                <div class="invite-card-head">
                  <div>
                    <span>{{ $t('奖励规则') }}</span>
                    <strong>{{ $t('HL交易区直推返佣') }}</strong>
                  </div>
                  <b>{{ $t('试玩不参与') }}</b>
                </div>
                <div class="reward-tier-grid">
                  <div v-for="item in inviteInfo.reward_configs || []" :key="item.id">
                    <span>{{ $t('满 ') }}{{ formatBalance(item.product_amount) }}U</span>
                    <strong>{{ formatPercent(item.reward_percent) }}</strong>
                  </div>
                </div>
              </div>

              <div class="invite-card">
                <div class="invite-card-head">
                  <div>
                    <span>{{ $t('邀请二维码') }}</span>
                    <strong>{{ $t('扫码或复制链接注册') }}</strong>
                  </div>
                  <button class="mini-action" @click="loadInviteInfo">{{ $t('刷新') }}</button>
                </div>
                <div class="qr-premium">
                  <img :src="inviteQrUrl" alt="邀请二维码">
                </div>
                <div class="invite-link-box">
                  <input ref="inviteLinkInput" :value="inviteLink" readonly @focus="selectInviteLink">
                </div>
                <button class="primary-action invite-copy" @click="copyInviteLink">{{ $t('复制邀请链接') }}</button>
              </div>

              <div class="invite-parent">
                <span>{{ $t('我的上级') }}</span>
                <strong>{{ inviteInfo.parent_invite_code || $t('无') }}</strong>
              </div>
            </div>

            <div v-if="currentPanel === 'team'" class="team-panel">
              <div class="team-hero">
                <span>{{ $t('当前会员等级') }}</span>
                <strong>{{ localizedMemberLevel(teamStats.member_level_name) }}</strong>
                <p>
                  <template v-if="teamStats.official_earning_preview && Number(teamStats.official_earning_preview.active_order_count) > 0">{{ $t('当前HL交易区日收益率：') }}</template>
                  <template v-else>{{ $t('购买HL交易区产品后显示收益率') }}</template>
                  {{ formatPercent(teamStats.official_earning_preview && teamStats.official_earning_preview.daily_rate) }}
                </p>
              </div>

              <div class="team-summary">
                <div>
                  <span>{{ $t('团队总人数') }}</span>
                  <strong>{{ teamStats.team_total_count || 0 }}</strong>
                </div>
                <div>
                  <span>{{ $t('直推人数') }}</span>
                  <strong>{{ teamStats.direct_count || 0 }}</strong>
                </div>
                <div>
                  <span>{{ $t('有效人数') }}</span>
                  <strong>{{ teamStats.effective_member_count || 0 }}</strong>
                </div>
              </div>

              <div class="level-progress" v-if="teamStats.next_level">
                <div class="level-progress-head">
                  <span>{{ $t('下一等级') }}</span>
                  <strong>{{ teamStats.next_level.level_name }}</strong>
                </div>
                <div class="level-progress-grid">
                  <div>
                    <span>{{ $t('正式购买') }}</span>
                    <strong>{{ formatBalance(teamStats.self_official_purchase_amount) }} / {{ formatBalance(teamStats.next_level.purchase_threshold) }} U</strong>
                  </div>
                  <div>
                    <span>{{ $t('有效直推') }}</span>
                    <strong>{{ teamStats.direct_effective_count || 0 }} / {{ teamStats.next_level.direct_required }} {{ $t('人') }}</strong>
                  </div>
                  <div>
                    <span>{{ $t('有效团队') }}</span>
                    <strong>{{ teamStats.effective_member_count || 0 }} / {{ teamStats.next_level.team_required }} {{ $t('人') }}</strong>
                  </div>
                  <div>
                    <span>{{ $t('升级收益率') }}</span>
                    <strong>{{ formatPercent(teamStats.next_level.yield_rate) }}</strong>
                  </div>
                  <div>
                    <span>{{ $t('升级奖励') }}</span>
                    <strong>{{ formatBalance(teamStats.next_level.upgrade_reward) }} U</strong>
                  </div>
                </div>
                <p>{{ $t('有效成员需') }}{{ $t('HL交易区产品') }} {{ $t('累计购买达到') }} {{ formatBalance(teamStats.effective_minimum_amount) }} U{{ $t('，HL体验区产品不计入。') }}</p>
              </div>

              <div class="team-section-head">
                <div>
                  <span>{{ $t('一级成员') }}</span>
                  <strong>{{ $t('我的直推列表') }}</strong>
                </div>
                <button class="mini-action" @click="refreshTeam">{{ $t('刷新') }}</button>
              </div>

              <div class="team-list" v-if="teamList.length">
                <div class="team-member" v-for="item in teamList" :key="item.id">
                  <div class="team-member-head">
                    <div class="team-avatar">
                      <img v-if="item.avatar" :src="assetUrl(item.avatar)" alt="成员头像">
                      <span v-else>{{ memberAvatar(item) }}</span>
                    </div>
                    <div class="team-identity">
                      <strong>{{ memberDisplayName(item) }}</strong>
                      <span>{{ maskChainAddress(item.address) }}</span>
                    </div>
                      <span class="team-level">{{ localizedMemberLevel(item.member_level_name) }}</span>
                  </div>
                </div>
              </div>
              <div class="empty-panel" v-else>
                <div class="empty-title">{{ $t('暂无直推成员') }}</div>
                <div class="empty-desc">{{ $t('通过邀请链接注册的一级成员会显示在这里。') }}</div>
              </div>
              <button class="load-more" v-if="hasMoreTeam" @click="loadMoreTeam">{{ $t('加载更多') }}</button>
            </div>
          </div>
        </div>
      </transition>
    </div>
  </div>
</template>

<script>
import { formatAmount, shortAddress } from '@/utils/web3'
import { loginWithWallet } from '@/utils/auth'
import * as api from '@/api'
import { formatChinaTime } from '@/utils/date'
import { translateText } from '@/i18n'
import DatePicker from '@/components/DatePicker.vue'

export default {
  name: 'SideDrawer',
  components: { DatePicker },
  data() {
    return {
      currentPanel: null,
      submitting: false,
      uploadingProof: false,
      creatingPayment: false,
      checkingPayment: false,
      walletPayEnabled: false,
      manualRechargeEnabled: false,
      paymentOrder: null,
      rechargeConfigs: [],
      withdrawConfig: { title: '提现说明', title_en: 'Withdrawal instructions', content: '', content_en: '', min_amount: 0, fee_percent: 0 },
      siteContent: { project_title: 'DApp Finance', project_title_en: 'DApp Finance', project_content: '', project_content_en: '' },
      inviteInfo: {},
      teamList: [],
      teamPage: 1,
      teamTotal: 0,
      teamStats: {
        team_total_count: 0,
        direct_count: 0,
        effective_member_count: 0,
        direct_effective_count: 0,
            member_level_name: '',
        member_yield_rate: 0,
        self_official_purchase_amount: 0,
        effective_minimum_amount: 1000,
        next_level: null,
        official_earning_preview: null
      },
      rechargeForm: { amount: '', tx_hash: '', screenshot: '' },
      withdrawForm: { amount: '', to_address: '' },
      orderTab: 'trial',
      orderList: [],
      orderPage: 1,
      orderTotal: 0,
      txList: [],
      txPage: 1,
      txTotal: 0,
      txFilters: { type: '', start_date: '', end_date: '' },
      withdrawList: []
    }
  },
  computed: {
    visible() { return this.$store.state.drawerVisible },
    isLoggedIn() { return this.$store.getters.isLoggedIn },
    balance() { return this.$store.getters.balance },
    totalEarned() { return this.$store.getters.totalEarned },
    officialEarningPreview() {
      return this.user && this.user.official_earning_preview
        ? this.user.official_earning_preview
        : null
    },
    availableEarnings() {
      const value = this.user && (this.user.earnings_balance ?? this.user.available_earnings)
      const parsedValue = Number(value)
      if (value !== undefined && value !== null && value !== '' && Number.isFinite(parsedValue)) {
        return Math.max(0, parsedValue)
      }
      const fallback = Number(this.totalEarned)
      return Number.isFinite(fallback) ? Math.max(0, fallback) : 0
    },
    user() { return this.$store.state.user },
    shortAddr() { return this.user ? shortAddress(this.user.address) : '' },
    avatarUrl() { return this.user && this.user.avatar ? this.assetUrl(this.user.avatar) : '' },
    defaultAvatarUrl() { return '/dapp-logo.png' },
    avatarText() {
      const address = this.user && this.user.address ? this.user.address : ''
      return address ? address.slice(2, 4).toUpperCase() : 'U'
    },
    memberRateText() {
      const preview = this.user?.official_earning_preview
      const rate = Number(preview?.daily_rate || 0)
      return rate > 0 ? this.$t(`HL交易区日收益率 ${this.formatPercent(rate)}`) : this.$t('等待HL交易区日收益率')
    },
    panelTitle() {
      const map = { recharge: '充值', withdraw: '提现', orders: '购买记录', transactions: '交易记录', invite: '邀请好友', team: '我的团队', project: '项目介绍' }
      return map[this.currentPanel] ? this.$t(map[this.currentPanel]) : ''
    },
    hasMoreOrders() { return this.orderList.length < this.orderTotal },
    hasMoreTx() { return this.txList.length < this.txTotal },
    hasMoreTeam() { return this.teamList.length < this.teamTotal },
    withdrawFeeAmount() {
      return Number(this.withdrawForm.amount || 0) * Number(this.withdrawConfig.fee_percent || 0) / 100
    },
    withdrawActualAmount() {
      return Math.max(0, Number(this.withdrawForm.amount || 0) - this.withdrawFeeAmount)
    },
    inviteCode() {
      return this.inviteInfo.invite_code || (this.user && this.user.invite_code) || ''
    },
    inviteLink() {
      const code = this.inviteCode
      if (!code) return window.location.origin + window.location.pathname
      return `${window.location.origin}${window.location.pathname}?invite=${code}#/`
    },
    inviteQrUrl() {
      return `https://api.qrserver.com/v1/create-qr-code/?size=220x220&margin=12&data=${encodeURIComponent(this.inviteLink)}`
    },
    localizedWithdrawContent() {
      const value = this.$store.state.locale === 'en-US'
        ? (this.withdrawConfig.content_en || this.withdrawConfig.content)
        : this.withdrawConfig.content
      return value ? translateText(value, this.$store.state.locale) : this.$t('请确认提现地址无误，提交后将进入后台审核流程。')
    },
    localizedProjectTitle() {
      const value = this.$store.state.locale === 'en-US'
        ? (this.siteContent.project_title_en || this.siteContent.project_title)
        : this.siteContent.project_title
      return value || 'DApp Finance'
    },
    localizedProjectContent() {
      const value = this.$store.state.locale === 'en-US'
        ? (this.siteContent.project_content_en || this.siteContent.project_content)
        : this.siteContent.project_content
      return value ? translateText(value, this.$store.state.locale) : ''
    }
  },
  watch: {
    visible(val) {
      if (val && this.isLoggedIn) this.refreshUserInfo()
    },
    user: {
      deep: true,
      handler(user) {
        if (user && user.address && this.currentPanel === 'withdraw') {
          this.withdrawForm.to_address = user.address
        }
      }
    },
    orderTab() { this.orderList = []; this.orderPage = 1; this.loadOrders() },
    currentPanel(val) {
      if (val === 'recharge') this.loadRechargeConfigs()
      if (val === 'withdraw') {
        this.withdrawForm.to_address = this.user && this.user.address ? this.user.address : ''
        this.refreshWalletInfo()
        this.loadWithdrawConfig()
        this.loadWithdrawRecords()
      }
      if (val === 'orders') { this.orderList = []; this.orderPage = 1; this.loadOrders() }
      if (val === 'transactions') { this.txList = []; this.txPage = 1; this.loadTxList() }
      if (val === 'invite') this.loadInviteInfo()
      if (val === 'team') this.refreshTeam()
      if (val === 'project') this.loadSiteContent()
    }
  },
  methods: {
    async loadSiteContent() {
      try {
        const res = await api.getSiteHome()
        if (res.code === 0 && res.data) this.siteContent = res.data
      } catch (e) {}
    },
    async refreshUserInfo() {
      const currentUser = this.$store.state.user
      if (!this.$store.getters.isLoggedIn || !currentUser || !currentUser.address) return
      const expectedAddress = String(currentUser.address).toLowerCase()
      try {
        const res = await api.getUserProfile()
        const activeUser = this.$store.state.user
        if (
          res.code === 0 &&
          res.data &&
          activeUser &&
          String(activeUser.address || '').toLowerCase() === expectedAddress &&
          String(res.data.address || '').toLowerCase() === expectedAddress
        ) {
          this.$store.commit('SET_USER', res.data)
        }
      } catch (e) {}
    },
    async refreshWalletInfo() {
      const currentUser = this.$store.state.user
      const expectedToken = this.$store.state.token
      if (!this.$store.getters.isLoggedIn || !currentUser || !currentUser.address) return
      const expectedAddress = String(currentUser.address).toLowerCase()
      try {
        const res = await api.getWalletInfo()
        const activeUser = this.$store.state.user
        if (
          res.code === 0 &&
          res.data &&
          activeUser &&
          this.$store.state.token === expectedToken &&
          String(activeUser.address || '').toLowerCase() === expectedAddress
        ) {
          this.$store.commit('SET_USER', {
            ...activeUser,
            balance: res.data.balance,
            earnings_balance: res.data.earnings_balance,
            total_earned: res.data.total_earned,
            available_earnings: res.data.available_earnings ?? res.data.earnings_balance
          })
        }
      } catch (e) {}
    },
    formatBalance(val) { return formatAmount(val) },
    earningsBalanceFromPreview(preview) {
      const value = Number(preview && preview.earnings_balance)
      return preview && preview.earnings_balance !== null && preview.earnings_balance !== undefined && Number.isFinite(value)
        ? Math.max(0, value)
        : this.availableEarnings
    },
    formatChinaTime,
    formatPercent(val) {
      return `${Number(val || 0).toLocaleString('en-US', { maximumFractionDigits: 2 })}%`
    },
    maskChainAddress(address) {
      if (!address) return '-'
      return address.length > 16 ? `${address.slice(0, 8)}****${address.slice(-6)}` : address
    },
    memberAvatar(item) {
      const source = item.username || item.address || 'U'
      return source.replace(/^0x/i, '').slice(0, 2).toUpperCase()
    },
    localizedMemberLevel(level) {
      return level ? this.$t(level) : this.$t('普通会员')
    },
    memberDisplayName(item) {
      if (item && item.username) return item.username
      return `${this.$t('用户 ')}${item && (item.invite_code || item.id) ? (item.invite_code || item.id) : ''}`
    },
    assetUrl(url) {
      if (!url) return ''
      if (/^(https?:\/\/|data:)/.test(url)) return url
      return url.startsWith('/uploads') ? url : `/uploads/${url.replace(/^\/+/, '')}`
    },
    close() { this.$store.dispatch('closeDrawer') },
    backToMenu() { this.currentPanel = null },
    goTo(panel) { this.currentPanel = panel },
    async connectAndClose() {
      try {
        await loginWithWallet(this.$store)
        this.$toast('登录成功', 'success')
        this.close()
        return
        const address = await getWalletAddress()
        const nonceRes = await api.getNonce(address)
        if (nonceRes.code !== 0) { this.$toast(nonceRes.message, 'error'); return }
        const message = `DApp Finance Login\nNonce: ${nonceRes.data.nonce}`
        const signature = await web3SignMessage(message)
        await this.$store.dispatch('login', { address, signature })
        this.$toast('登录成功', 'success')
        this.close()
      } catch (err) {
        this.$toast('登录失败', 'error')
      }
    },
    logout() { this.$store.dispatch('logout'); this.close(); this.$toast('已退出登录', 'info') },
    orderStatusText(item) {
      if (item.status === 1) return this.$t('进行中')
      if (item.status === 0) return this.$t('已完成')
      if (item.status === -1) return this.$t('已取消')
      return this.$t('未知')
    },
    txTypeLabel(type) {
      const map = { recharge: '充值', withdraw: '提现', buy_trial: '购买HL体验区', buy_official: '购买HL交易区', earning: '收益入账', referral_reward: '直推奖励', level_reward: '等级奖励' }
      return map[type] ? this.$t(map[type]) : type
    },
    txRemark(remark) {
      const value = String(remark || '-')
        .replace(/购买试玩产品/g, '购买HL体验区产品')
        .replace(/购买正式产品/g, '购买HL交易区产品')
        .replace(/试玩产品每日收益/g, 'HL体验区每日收益')
        .replace(/正式产品每日收益/g, 'HL交易区每日收益')
        .replace(/试玩产品/g, 'HL体验区产品')
        .replace(/正式产品/g, 'HL交易区产品')
      return this.$t(value)
    },
    localizedOrderProductName(item) {
      if (!item) return this.$t('产品')
      const value = this.$store.state.locale === 'en-US'
        ? (item.product_name_en || item.product_name)
        : item.product_name
      return value || this.$t('产品')
    },
    async loadRechargeConfigs() {
      try {
        const [configRes, paymentRes] = await Promise.all([
          api.getRechargeConfigs(),
          api.getWalletPaymentStatus()
        ])
        if (configRes.code === 0) this.rechargeConfigs = configRes.data || []
        this.walletPayEnabled = paymentRes.code === 0 && Boolean(paymentRes.data && paymentRes.data.enabled)
        this.manualRechargeEnabled = paymentRes.code === 0 && Boolean(paymentRes.data && paymentRes.data.manual_enabled)
      } catch (e) {}
    },
    async loadWithdrawConfig() {
      try {
        const res = await api.getWithdrawConfig()
        if (res.code === 0 && res.data) this.withdrawConfig = res.data
      } catch (e) {}
    },
    async loadWithdrawRecords() {
      try {
        const res = await api.getWithdrawRecords({ page: 1, pageSize: 20 })
        if (res.code === 0 && res.data) this.withdrawList = res.data.list || []
      } catch (e) {}
    },
    withdrawStatusText(status) {
      if (Number(status) === 0) return this.$t('审核中')
      if (Number(status) === 1) return this.$t('已通过')
      return this.$t('已拒绝')
    },
    async copyAddress(address) {
      return this.copyText(address, '地址已复制')
    },
    selectAddress(event) {
      const input = event && event.target
      if (!input) return
      input.focus()
      input.select()
      input.setSelectionRange(0, input.value.length)
    },
    async copyRechargeAddress(address, event) {
      const button = event && event.currentTarget
      const input = button && button.parentElement
        ? button.parentElement.querySelector('.address-text-input')
        : null
      if (input) this.selectAddress({ target: input })

      let copied = false
      try { copied = document.execCommand('copy') } catch (e) {}
      if (copied) return this.$toast('地址已复制', 'success')

      try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(address)
          return this.$toast('地址已复制', 'success')
        }
      } catch (e) {}

      if (input) this.selectAddress({ target: input })
      this.$toast('地址已选中，请长按复制', 'info')
    },
    async copyText(text, message = '复制成功') {
      try {
        if (navigator.clipboard && window.isSecureContext) {
          await navigator.clipboard.writeText(text)
        } else {
          const input = document.createElement('textarea')
          input.value = text
          input.style.position = 'fixed'
          input.setAttribute('readonly', '')
          input.style.top = '0'
          input.style.left = '0'
          input.style.width = '1px'
          input.style.height = '1px'
          input.style.opacity = '0.01'
          document.body.appendChild(input)
          input.focus()
          input.select()
          input.setSelectionRange(0, input.value.length)
          const copied = document.execCommand('copy')
          document.body.removeChild(input)
          if (!copied) throw new Error('clipboard unavailable')
        }
        this.$toast(message, 'success')
      } catch (e) {
        this.$toast('复制失败，请手动复制', 'error')
      }
    },
    selectInviteLink(event) {
      event.target.select()
      event.target.setSelectionRange(0, event.target.value.length)
    },
    async copyText(text, message = '复制成功') {
      try {
        if (navigator.clipboard && window.isSecureContext) {
          await navigator.clipboard.writeText(text)
        } else {
          const input = document.createElement('textarea')
          input.value = text
          input.setAttribute('readonly', '')
          input.style.position = 'fixed'
          input.style.top = '0'
          input.style.left = '0'
          input.style.width = '1px'
          input.style.height = '1px'
          input.style.opacity = '0.01'
          document.body.appendChild(input)
          input.focus()
          input.select()
          input.setSelectionRange(0, input.value.length)
          const copied = document.execCommand('copy')
          document.body.removeChild(input)
          if (!copied) throw new Error('clipboard unavailable')
        }
        this.$toast(message, 'success')
      } catch (e) {
        window.prompt('请长按复制以下内容', text)
      }
    },
    async copyInviteLink() {
      const input = this.$refs.inviteLinkInput
      const text = this.inviteLink
      if (!input) return
      input.focus()
      input.select()
      input.setSelectionRange(0, input.value.length)
      let copied = false
      try { copied = document.execCommand('copy') } catch (e) {}
      if (copied) return this.$toast('邀请链接已复制', 'success')
      try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(text)
          return this.$toast('邀请链接已复制', 'success')
        }
      } catch (e) {}
      this.$toast('链接已选中，请长按链接复制', 'info')
    },
    async loadInviteInfo() {
      try {
        const res = await api.getInviteInfo()
        if (res.code === 0) {
          this.inviteInfo = res.data || {}
          if (this.user) {
            this.$store.commit('SET_USER', { ...this.user, invite_code: this.inviteInfo.invite_code })
          }
        }
      } catch (e) {}
    },
    async refreshTeam() {
      this.teamList = []
      this.teamPage = 1
      this.teamTotal = 0
      await this.loadTeam()
    },
    async loadTeam() {
      try {
        const res = await api.getDirectTeam({ page: this.teamPage, pageSize: 10 })
        if (res.code === 0) {
          this.teamList = [...this.teamList, ...(res.data.list || [])]
          this.teamTotal = Number(res.data.total || 0)
          this.teamStats = {
            team_total_count: Number(res.data.team_total_count || 0),
            direct_count: Number(res.data.direct_count || 0),
            effective_member_count: Number(res.data.effective_member_count || 0),
            direct_effective_count: Number(res.data.direct_effective_count || 0),
            member_level: Number(res.data.member_level ?? -1),
            member_level_name: res.data.member_level_name || '普通会员',
            member_yield_rate: Number(res.data.member_yield_rate || 0),
            self_official_purchase_amount: Number(res.data.self_official_purchase_amount || 0),
            effective_minimum_amount: Number(res.data.effective_minimum_amount || 0),
            next_level: res.data.next_level || null,
            official_earning_preview: res.data.official_earning_preview || null
          }
          if (this.user) {
            this.$store.commit('SET_USER', {
              ...this.user,
              member_level: this.teamStats.member_level,
              member_level_name: this.teamStats.member_level_name,
              member_yield_rate: this.teamStats.member_yield_rate,
              official_earning_preview: this.teamStats.official_earning_preview
            })
          }
        }
      } catch (e) {}
    },
    loadMoreTeam() {
      this.teamPage++
      this.loadTeam()
    },
    async handleProofChange(event) {
      const file = event.target.files && event.target.files[0]
      if (!file) return
      const data = new FormData()
      data.append('file', file)
      this.uploadingProof = true
      try {
        const res = await api.uploadUserImage(data)
        if (res.code === 0) {
          this.rechargeForm.screenshot = res.data.url
          this.$toast('凭证上传成功', 'success')
        } else {
          this.$toast(res.message, 'error')
        }
      } catch (e) {
        this.$toast('凭证上传失败', 'error')
      }
      this.uploadingProof = false
    },
    paymentStatusText(status) {
      const map = {
        creating: '创建中',
        pending: '待支付',
        paid: '已到账',
        success: '已到账',
        completed: '已到账',
        expired: '已过期',
        failed: '失败',
        cancelled: '已取消'
      }
      return map[status] ? this.$t(map[status]) : (status || this.$t('待支付'))
    },
    paymentStatusClass(status) {
      if (['paid', 'success', 'completed'].includes(status)) return 'is-paid'
      if (['expired', 'failed', 'cancelled'].includes(status)) return 'is-failed'
      return 'is-pending'
    },
    async createPaymentOrder() {
      const amount = String(this.rechargeForm.amount || '').trim()
      if (!amount || Number(amount) <= 0) return this.$toast('请输入正确的充值金额', 'error')
      this.creatingPayment = true
      try {
        const res = await api.createWalletPaymentOrder({ amount })
        if (res.code === 0) {
          this.paymentOrder = res.data
          this.$toast('充值订单已创建', 'success')
        } else {
          this.$toast(res.message, 'error')
        }
      } catch (e) {
        this.$toast('创建充值订单失败', 'error')
      }
      this.creatingPayment = false
    },
    async checkPaymentOrder() {
      if (!this.paymentOrder || !this.paymentOrder.merchant_order_no) return
      this.checkingPayment = true
      try {
        const res = await api.queryWalletPaymentOrder(this.paymentOrder.merchant_order_no)
        if (res.code === 0) {
          this.paymentOrder = { ...this.paymentOrder, ...res.data }
          if (['paid', 'success', 'completed'].includes(this.paymentOrder.status)) {
            const profile = await api.getUserProfile()
            if (profile.code === 0) this.$store.commit('SET_USER', profile.data)
            this.$toast('充值已到账', 'success')
          } else {
            this.$toast('暂未到账，请稍后再查', 'info')
          }
        } else {
          this.$toast(res.message, 'error')
        }
      } catch (e) {
        this.$toast('查询充值状态失败', 'error')
      }
      this.checkingPayment = false
    },
    resetPaymentOrder() {
      this.paymentOrder = null
      this.rechargeForm.amount = ''
    },
    async loadOrders() {
      try {
        const fn = this.orderTab === 'trial' ? api.getTrialOrders : api.getOfficialOrders
        const res = await fn({ page: this.orderPage, pageSize: 10 })
        if (res.code === 0) { this.orderList = [...this.orderList, ...res.data.list]; this.orderTotal = res.data.total }
      } catch (e) {}
    },
    loadMoreOrders() { this.orderPage++; this.loadOrders() },
    async loadTxList() {
      try {
        const params = { page: this.txPage, pageSize: 10 }
        if (this.txFilters.type) params.type = this.txFilters.type
        if (this.txFilters.start_date) params.start_date = this.txFilters.start_date
        if (this.txFilters.end_date) params.end_date = this.txFilters.end_date
        const res = await api.getTransactions(params)
        if (res.code === 0) { this.txList = [...this.txList, ...res.data.list]; this.txTotal = res.data.total }
      } catch (e) {}
    },
    loadMoreTx() { this.txPage++; this.loadTxList() },
    applyTxFilters() {
      const datePattern = /^\d{4}-\d{2}-\d{2}$/
      if ((this.txFilters.start_date && !datePattern.test(this.txFilters.start_date)) || (this.txFilters.end_date && !datePattern.test(this.txFilters.end_date))) {
        return this.$toast('日期格式错误', 'error')
      }
      if (
        this.txFilters.start_date &&
        this.txFilters.end_date &&
        this.txFilters.start_date > this.txFilters.end_date
      ) {
        return this.$toast('开始日期不能晚于结束日期', 'error')
      }
      this.txList = []
      this.txPage = 1
      this.txTotal = 0
      this.loadTxList()
    },
    resetTxFilters() {
      this.txFilters = { type: '', start_date: '', end_date: '' }
      this.applyTxFilters()
    },
    async submitRecharge() {
      if (!this.rechargeForm.amount || Number(this.rechargeForm.amount) <= 0) return this.$toast('请输入正确的充值金额', 'error')
      if (!this.rechargeForm.screenshot) return this.$toast('请上传充值凭证', 'error')
      this.submitting = true
      try {
        const res = await api.applyRecharge(this.rechargeForm)
        if (res.code === 0) {
          this.$toast('充值申请已提交', 'success')
          this.rechargeForm = { amount: '', tx_hash: '', screenshot: '' }
          this.currentPanel = 'withdraw'
        } else this.$toast(res.message, 'error')
      } catch (e) { this.$toast('提交失败', 'error') }
      this.submitting = false
    },
    async submitWithdraw() {
      if (!this.withdrawForm.amount || Number(this.withdrawForm.amount) <= 0) return this.$toast('请输入正确的提现金额', 'error')
      if (Number(this.withdrawForm.amount) < Number(this.withdrawConfig.min_amount || 0)) {
        return this.$toast(`最低提现 ${this.formatBalance(this.withdrawConfig.min_amount)} USDT`, 'error')
      }
      if (!this.withdrawForm.to_address) return this.$toast('请输入提现地址', 'error')
      if (Number(this.withdrawForm.amount) > Number(this.availableEarnings)) return this.$toast('可提现收益金额不足', 'error')
      this.submitting = true
      try {
        const res = await api.applyWithdraw(this.withdrawForm)
        if (res.code === 0) {
          this.$toast('提现申请已提交', 'success')
          this.withdrawForm = {
            amount: '',
            to_address: this.user && this.user.address ? this.user.address : ''
          }
          this.currentPanel = null
          const p = await api.getUserProfile()
          if (p.code === 0) this.$store.commit('SET_USER', p.data)
          await this.loadWithdrawRecords()
        } else this.$toast(res.message, 'error')
      } catch (e) { this.$toast('提交失败', 'error') }
      this.submitting = false
    }
  }
}
</script>

<style scoped>
.drawer-overlay {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.45);
  backdrop-filter: blur(4px);
  z-index: 2000;
}
.drawer-panel {
  position: absolute;
  top: 0;
  right: -360px;
  width: 340px;
  height: 100%;
  background: #f7f9fc;
  box-shadow: -8px 0 40px rgba(15, 23, 42, 0.18);
  transition: right 0.35s cubic-bezier(0.16, 1, 0.3, 1);
  overflow-y: auto;
  display: flex;
  flex-direction: column;
}
.drawer-panel.open { right: 0; }
.drawer-header {
  position: relative;
  background: linear-gradient(135deg, #172554, #2563eb 52%, #14b8a6);
  padding: 36px 24px 28px;
  color: #fff;
}
.drawer-avatar-wrap { position: relative; display: inline-block; margin-bottom: 10px; }
.drawer-profile-main {
  display: flex;
  align-items: center;
  gap: 13px;
  min-width: 0;
  margin-bottom: 16px;
  padding-right: 34px;
}
.drawer-profile-main .drawer-avatar-wrap {
  flex-shrink: 0;
  margin-bottom: 0;
}
.drawer-profile-info {
  min-width: 0;
  flex: 1;
}
.drawer-avatar {
  width: 52px;
  height: 52px;
  border-radius: 50%;
  overflow: hidden;
  background: rgba(255,255,255,0.18);
  backdrop-filter: blur(10px);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 800;
}
.drawer-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.avatar-badge {
  position: absolute;
  bottom: 2px;
  right: 2px;
  width: 14px;
  height: 14px;
  background: #22c55e;
  border: 2px solid #2563eb;
  border-radius: 50%;
}
.drawer-address { font-size: 12px; opacity: 0.82; word-break: break-all; margin-bottom: 16px; }
.drawer-profile-info .drawer-address {
  margin-bottom: 0;
  line-height: 1.45;
  text-align: left;
}
.drawer-level-row {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  flex-wrap: wrap;
  gap: 8px;
  margin: 8px 0 0;
}
.drawer-level-row span {
  padding: 4px 9px;
  border-radius: 999px;
  background: rgba(245, 158, 11, 0.2);
  color: #fbbf24;
  font-size: 11px;
  font-weight: 900;
}
.drawer-level-row em {
  color: rgba(255, 255, 255, 0.76);
  font-size: 11px;
  font-style: normal;
  font-weight: 700;
}
.drawer-stats {
  display: flex;
  background: rgba(255,255,255,0.12);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255,255,255,0.12);
  border-radius: 12px;
  padding: 14px 0;
}
.stat-box { flex: 1; text-align: center; }
.stat-label { font-size: 11px; opacity: 0.68; margin-bottom: 4px; letter-spacing: 0; }
.stat-val { font-size: 20px; font-weight: 850; }
.balance-color, .earn-color { color: #fff; }
.stat-sub { font-size: 10px; opacity: 0.55; }
.stat-divider { width: 1px; background: rgba(255,255,255,0.16); }
.drawer-close {
  position: absolute;
  top: 14px;
  right: 14px;
  background: rgba(255,255,255,0.16);
  border: none;
  color: #fff;
  width: 34px;
  height: 34px;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}
.drawer-close:hover { background: rgba(255,255,255,0.26); }
.guest-avatar { font-size: 14px; }
.guest-tip { font-size: 12px; opacity: 0.7; margin-top: 8px; }
.drawer-body { flex: 1; padding: 16px 0; overflow-y: auto; }
.drawer-empty-body { display: flex; align-items: center; justify-content: center; }
.drawer-empty { text-align: center; color: #64748b; }
.drawer-empty .empty-icon { font-size: 28px; margin-bottom: 12px; }
.personal-earning-preview {
  margin: 0 16px 14px;
  padding: 14px;
  border: 1px solid var(--tech-line);
  border-radius: 8px;
  background: var(--tech-surface);
}
.personal-earning-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 13px;
}
.personal-earning-head span,
.personal-earning-formula span,
.personal-earning-result span {
  display: block;
  color: var(--tech-muted);
  font-size: 10px;
  font-weight: 800;
}
.personal-earning-head strong {
  display: block;
  margin-top: 3px;
  color: var(--tech-text);
  font-size: 14px;
  font-weight: 900;
}
.personal-earning-head em {
  flex-shrink: 0;
  padding: 5px 8px;
  border: 1px solid var(--tech-line-soft);
  border-radius: 999px;
  color: var(--tech-mint);
  background: #173a24;
  font-size: 11px;
  font-style: normal;
  font-weight: 900;
}
.personal-earning-formula {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr) auto minmax(0, 1fr);
  align-items: center;
  gap: 6px;
}
.personal-earning-formula > div { min-width: 0; }
.personal-earning-formula strong {
  display: block;
  margin-top: 3px;
  color: var(--tech-text);
  font-size: 12px;
  font-weight: 900;
  overflow-wrap: anywhere;
}
.personal-earning-formula > b {
  color: var(--tech-mint);
  font-size: 14px;
}
.personal-earning-result {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 10px;
  margin-top: 12px;
  padding-top: 10px;
  border-top: 1px solid var(--tech-line);
}
.personal-earning-result strong {
  color: var(--tech-mint);
  font-size: 18px;
  font-weight: 950;
}
.personal-earning-empty {
  padding: 12px;
  border: 1px dashed var(--tech-line-soft);
  border-radius: 6px;
  color: var(--tech-muted);
  background: var(--tech-surface-2);
  font-size: 11px;
  line-height: 1.5;
  text-align: center;
}
.menu-group { padding: 0 16px; margin-bottom: 8px; }
.menu-label {
  font-size: 11px;
  font-weight: 800;
  color: #94a3b8;
  letter-spacing: 0;
  padding: 8px 4px 6px;
}
.menu-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  cursor: pointer;
  border-radius: 12px;
  transition: background 0.2s, transform 0.2s;
  margin-bottom: 4px;
}
.menu-item:hover { background: #eef6ff; }
.menu-item:active { transform: scale(0.98); }
.menu-icon-wrap {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.menu-icon-wrap.green { background: #dcfce7; color: #16a34a; }
.menu-icon-wrap.orange { background: #ffedd5; color: #ea580c; }
.menu-icon-wrap.blue { background: #dbeafe; color: #2563eb; }
.menu-icon-wrap.purple { background: #ede9fe; color: #7c3aed; }
.menu-icon-wrap.teal { background: #ccfbf1; color: #0f766e; }
.menu-icon-wrap.indigo { background: #e0e7ff; color: #4338ca; }
.menu-icon-wrap.red { background: #fee2e2; color: #dc2626; }
.menu-text { flex: 1; font-size: 14px; font-weight: 650; color: #1e293b; }
.menu-arrow { color: #94a3b8; }
.menu-logout .menu-text { color: #dc2626; }
.menu-group-last { margin-top: 24px; border-top: 1px solid #e2e8f0; padding-top: 16px; }

.sub-panel {
  position: absolute;
  inset: 0;
  background: #f7f9fc;
  z-index: 10;
  display: flex;
  flex-direction: column;
}
.sub-header {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 16px 16px 12px;
  background: rgba(255,255,255,0.92);
  backdrop-filter: blur(14px);
  border-bottom: 1px solid #e9eef5;
}
.sub-back {
  background: #eef6ff;
  border: none;
  cursor: pointer;
  color: #2563eb;
  width: 34px;
  height: 34px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}
.sub-title { font-size: 16px; font-weight: 800; color: var(--tech-text); }
.sub-content {
  flex: 1;
  overflow-y: auto;
  padding: 16px;
  background:
    linear-gradient(180deg, #eef6ff 0, rgba(238,246,255,0) 180px),
    #f7f9fc;
}
.action-panel { padding-bottom: 86px; }
.wallet-hero {
  border-radius: 8px;
  padding: 18px;
  background: linear-gradient(135deg, #0f766e, #2563eb);
  color: #fff;
  box-shadow: 0 12px 30px rgba(37, 99, 235, 0.18);
  margin-bottom: 18px;
}
.withdraw-hero { background: linear-gradient(135deg, #7c2d12, #ea580c); }
.project-panel { padding-bottom: 24px; }
.project-cover { padding: 28px 22px; margin-bottom: 16px; border-radius: 12px; background: linear-gradient(135deg, #101512, #243b2b); color: #fff; }
.project-cover span { display: block; margin-bottom: 8px; color: #a9eeb4; font-size: 11px; font-weight: 900; letter-spacing: 2px; }
.project-cover strong { display: block; color: #f2fff4; font-size: 23px; font-weight: 950; }
.project-copy { padding: 18px; border: 1px solid #dceee0; border-radius: 10px; background: #fbfffb; color: #3f5544; line-height: 1.8; overflow-wrap: anywhere; }
.project-copy ::v-deep h1,
.project-copy ::v-deep h2,
.project-copy ::v-deep h3 { margin: 0 0 10px; color: #101512; }
.project-copy ::v-deep p { margin: 0 0 10px; }
.wallet-hero span { display: block; font-size: 12px; opacity: 0.76; margin-bottom: 4px; }
.wallet-hero strong { display: block; font-size: 26px; line-height: 1.15; font-weight: 900; }
.section-title {
  font-size: 13px;
  font-weight: 800;
  color: #334155;
  margin: 0 2px 10px;
}
.recharge-address-list { display: flex; flex-direction: column; gap: 12px; margin-bottom: 16px; }
.recharge-address-card {
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 14px;
  background: #fff;
  box-shadow: 0 6px 18px rgba(15, 23, 42, 0.04);
}
.address-main {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 12px;
  align-items: start;
}
.address-network { font-size: 13px; font-weight: 850; color: var(--tech-text); margin-bottom: 5px; }
.address-text {
  font-size: 12px;
  color: #64748b;
  word-break: break-all;
  line-height: 1.45;
}
.address-text-input {
  display: block;
  width: 100%;
  min-width: 0;
  padding: 0;
  border: 0;
  outline: 0;
  background: transparent;
  font: inherit;
  color: inherit;
  user-select: text;
  -webkit-user-select: text;
}
.mini-action {
  border: none;
  background: #eff6ff;
  color: #2563eb;
  border-radius: 8px;
  padding: 7px 10px;
  font-size: 12px;
  font-weight: 800;
  cursor: pointer;
}
.mini-action:active { transform: scale(0.98); }
.qr-wrap {
  margin-top: 12px;
  padding: 12px;
  border-radius: 8px;
  background: #f8fafc;
  display: flex;
  justify-content: center;
}
.qr-image {
  width: 142px;
  aspect-ratio: 1;
  object-fit: contain;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #fff;
}
.config-empty {
  padding: 14px;
  border-radius: 8px;
  background: #fff7ed;
  color: #c2410c;
  font-size: 13px;
  margin-bottom: 16px;
  border: 1px solid #fed7aa;
}
.form-stack { display: flex; flex-direction: column; gap: 12px; }
.field-card {
  display: block;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 14px;
  box-shadow: 0 6px 18px rgba(15, 23, 42, 0.04);
}
.field-label {
  display: block;
  font-size: 12px;
  font-weight: 800;
  color: #64748b;
  margin-bottom: 8px;
}
.field-control {
  display: flex;
  align-items: center;
  gap: 8px;
  height: 44px;
  border-radius: 8px;
  background: #f8fafc;
  border: 1px solid transparent;
  padding: 0 12px;
  transition: border-color 0.2s, background 0.2s, box-shadow 0.2s;
}
.field-control:focus-within {
  background: #fff;
  border-color: #60a5fa;
  box-shadow: 0 0 0 3px rgba(96, 165, 250, 0.16);
}
.field-control input {
  min-width: 0;
  flex: 1;
  border: none;
  outline: none;
  background: transparent;
  color: var(--tech-text);
  font-size: 20px;
  font-weight: 850;
}
.field-control input::placeholder { color: #cbd5e1; }
.field-control em {
  font-style: normal;
  font-size: 12px;
  font-weight: 850;
  color: #2563eb;
  background: #eff6ff;
  border-radius: 999px;
  padding: 5px 8px;
  flex-shrink: 0;
}
.address-input input {
  font-size: 13px;
  font-weight: 650;
}
.address-input { height: auto; min-height: 58px; padding-top: 10px; padding-bottom: 10px; }
.address-input textarea {
  display: block;
  width: 100%;
  min-width: 0;
  min-height: 38px;
  padding: 0;
  border: 0;
  outline: 0;
  resize: vertical;
  background: transparent;
  color: var(--tech-text);
  font: 650 13px/1.5 monospace;
  overflow-wrap: anywhere;
}
.address-input textarea::placeholder { color: #cbd5e1; }
.upload-card {
  display: block;
  position: relative;
  background: #fff;
  border: 1.5px dashed #b6c4d7;
  border-radius: 8px;
  padding: 16px;
  cursor: pointer;
  transition: border-color 0.2s, background 0.2s;
}
.upload-card:hover { border-color: #60a5fa; background: #f8fbff; }
.upload-card.uploaded { border-style: solid; border-color: #86efac; background: #f7fef9; }
.upload-card input { display: none; }
.upload-title {
  display: block;
  font-size: 14px;
  font-weight: 850;
  color: var(--tech-text);
  margin-bottom: 3px;
}
.upload-desc {
  display: block;
  font-size: 12px;
  color: #64748b;
}
.proof-preview {
  display: block;
  width: 100%;
  max-height: 180px;
  object-fit: contain;
  margin-top: 12px;
  border: 1px solid #dbe4ef;
  border-radius: 8px;
  background: #fff;
}
.form-tips {
  margin: 12px 2px 0;
  font-size: 12px;
  line-height: 1.55;
  color: #64748b;
}
.withdraw-summary {
  margin-top: 12px;
  padding: 14px;
  border: 1px solid #dbe4ef;
  border-radius: 8px;
  background: #fff;
}
.withdraw-summary > div {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}
.withdraw-summary > div + div {
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px solid #eef2f7;
}
.withdraw-summary span {
  color: #64748b;
  font-size: 12px;
  font-weight: 750;
}
.withdraw-summary strong {
  color: #dc2626;
  font-size: 14px;
  font-weight: 900;
}
.withdraw-summary .withdraw-arrival strong {
  color: #059669;
  font-size: 18px;
}
.withdraw-history { margin-top: 18px; }
.withdraw-history-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 11px 12px;
  margin-bottom: 8px;
  border: 1px solid var(--tech-line);
  border-radius: 7px;
  background: var(--tech-surface);
}
.withdraw-history-item strong { display: block; color: var(--tech-text); font-size: 13px; }
.withdraw-history-item span { display: block; margin-top: 4px; color: var(--tech-muted); font-size: 10px; }
.withdraw-history-item em { flex-shrink: 0; font-size: 11px; font-style: normal; font-weight: 800; }
.withdraw-status-0 { color: #f0d985; }
.withdraw-status-1 { color: var(--tech-mint); }
.withdraw-status--1 { color: #ff9d9d; }
.sticky-actions {
  position: sticky;
  bottom: 0;
  margin: 18px -16px -16px;
  padding: 14px 16px 16px;
  background: linear-gradient(180deg, rgba(247,249,252,0), #f7f9fc 28%);
}
.primary-action {
  width: 100%;
  height: 46px;
  border: none;
  border-radius: 8px;
  color: #fff;
  background: linear-gradient(135deg, #2563eb, #1d4ed8);
  box-shadow: 0 10px 22px rgba(37, 99, 235, 0.24);
  font-size: 15px;
  font-weight: 850;
  cursor: pointer;
}
.success-action { background: linear-gradient(135deg, #059669, #0f766e); box-shadow: 0 10px 22px rgba(5, 150, 105, 0.22); }
.payment-status-card {
  background: #fff;
  border: 1px solid #dbe4ef;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 12px;
}
.payment-status-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}
.payment-status-row span {
  display: block;
  color: #64748b;
  font-size: 12px;
  font-weight: 800;
  margin-bottom: 4px;
}
.payment-status-row strong {
  color: var(--tech-text);
  font-size: 22px;
  font-weight: 950;
}
.payment-status {
  flex-shrink: 0;
  padding: 5px 9px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 900;
}
.payment-status.is-pending { color: #b45309; background: #fef3c7; }
.payment-status.is-paid { color: #047857; background: #d1fae5; }
.payment-status.is-failed { color: #b91c1c; background: #fee2e2; }
.payment-meta {
  margin-top: 10px;
  color: #64748b;
  font-size: 12px;
}
.payment-address-card { margin-bottom: 12px; }
.payment-link {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 42px;
  border-radius: 8px;
  background: #eff6ff;
  color: #2563eb;
  font-size: 13px;
  font-weight: 850;
  text-decoration: none;
  margin-bottom: 12px;
}
.payment-order-no {
  padding: 10px 12px;
  border-radius: 8px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  color: #64748b;
  font-size: 11px;
  word-break: break-all;
}
.payment-paid-tip { color: #047857; font-weight: 750; }
.payment-actions {
  display: grid;
  grid-template-columns: 108px minmax(0, 1fr);
  gap: 10px;
}
.secondary-action {
  height: 46px;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  background: #fff;
  color: #475569;
  font-size: 13px;
  font-weight: 850;
  cursor: pointer;
}
.secondary-action:disabled { opacity: 0.58; cursor: not-allowed; }
.primary-action:disabled {
  opacity: 0.58;
  cursor: not-allowed;
  box-shadow: none;
}
.primary-action:active:not(:disabled) { transform: translateY(1px); }
.segmented-tabs {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4px;
  background: #e8eef7;
  border-radius: 8px;
  padding: 4px;
  margin-bottom: 14px;
}
.segmented-tabs button {
  border: none;
  height: 34px;
  border-radius: 6px;
  background: transparent;
  color: #64748b;
  font-weight: 800;
  cursor: pointer;
}
.segmented-tabs button.active {
  background: #fff;
  color: #2563eb;
  box-shadow: 0 4px 12px rgba(15, 23, 42, 0.08);
}
.modern-list { padding-bottom: 20px; }
.transaction-filters {
  margin-bottom: 14px;
  padding: 12px;
  border: 1px solid #dbe4f0;
  border-radius: 8px;
  background: #f8fafc;
}
.transaction-filters select,
.transaction-filters input {
  width: 100%;
  height: 38px;
  box-sizing: border-box;
  border: 1px solid #cbd5e1;
  border-radius: 7px;
  background: #fff;
  color: #1e293b;
  font-size: 12px;
  outline: none;
}
.transaction-filters select { padding: 0 10px; }
.transaction-filters input { padding: 0 8px; }
.transaction-filters select:focus,
.transaction-filters input:focus {
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}
.transaction-date-range {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  margin-top: 9px;
}
.transaction-date-range label span {
  display: block;
  margin: 0 0 5px 2px;
  color: #64748b;
  font-size: 10px;
  font-weight: 800;
}
.transaction-filter-actions {
  display: grid;
  grid-template-columns: 88px 1fr;
  gap: 8px;
  margin-top: 10px;
}
.transaction-filter-actions button {
  height: 38px;
  border-radius: 7px;
  font-size: 12px;
  font-weight: 850;
  cursor: pointer;
}
.filter-reset {
  border: 1px solid #cbd5e1;
  background: #fff;
  color: #475569;
}
.filter-submit {
  border: 1px solid #2563eb;
  background: #2563eb;
  color: #fff;
}
.record-list { display: flex; flex-direction: column; gap: 12px; }
.record-card {
  background: #fff;
  border-radius: 8px;
  padding: 14px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 6px 18px rgba(15, 23, 42, 0.04);
}
.record-header { display: flex; justify-content: space-between; align-items: center; gap: 10px; }
.record-name { font-size: 14px; font-weight: 850; color: var(--tech-text); overflow-wrap: anywhere; }
.record-amount { font-size: 16px; font-weight: 900; color: #dc2626; white-space: nowrap; }
.record-amount.positive { color: #059669; }
.record-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #eef2f7;
}
.record-info {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 3px;
  font-size: 12px;
}
.record-info span { color: #94a3b8; }
.record-info strong {
  color: #1e293b;
  font-size: 13px;
  font-weight: 850;
  overflow-wrap: anywhere;
}
.green { color: #059669 !important; }
.record-footer { display: flex; justify-content: space-between; align-items: center; gap: 8px; margin-top: 12px; }
.record-date { font-size: 11px; color: #94a3b8; }
.record-tag, .record-type-badge {
  font-size: 11px;
  padding: 4px 9px;
  border-radius: 999px;
  font-weight: 850;
  white-space: nowrap;
}
.tag-1 { background: #dbeafe; color: #2563eb; }
.tag-0 { background: #dcfce7; color: #16a34a; }
.tag--1 { background: #ffedd5; color: #ea580c; }
.type-recharge { background: #dcfce7; color: #16a34a; }
.type-withdraw { background: #ffedd5; color: #ea580c; }
.type-buy_trial, .type-buy_official { background: #dbeafe; color: #2563eb; }
.type-earning { background: #fef3c7; color: #b45309; }
.type-referral_reward { background: #d1fae5; color: #047857; }
.type-level_reward { background: #ede9fe; color: #6d28d9; }
.empty-panel {
  text-align: center;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 32px 18px;
  color: #64748b;
}
.empty-title { font-size: 15px; font-weight: 850; color: #334155; margin-bottom: 6px; }
.empty-desc { font-size: 12px; line-height: 1.5; }
.load-more {
  width: 100%;
  margin-top: 12px;
  height: 40px;
  border: none;
  border-radius: 8px;
  background: #eff6ff;
  color: #2563eb;
  cursor: pointer;
  font-size: 13px;
  font-weight: 850;
}
.invite-panel, .team-panel { padding-bottom: 24px; }
.invite-hero {
  padding: 22px;
  border-radius: 8px;
  background:
    radial-gradient(circle at top right, rgba(20, 184, 166, 0.45), transparent 42%),
    linear-gradient(135deg, #061009, #12321d 58%, #1b4a2d);
  color: #fff;
  box-shadow: 0 18px 36px rgba(37, 99, 235, 0.22);
  margin-bottom: 14px;
}
.invite-hero span {
  display: block;
  font-size: 12px;
  opacity: 0.72;
  margin-bottom: 4px;
}
.invite-hero strong {
  display: block;
  font-size: 42px;
  line-height: 1;
  font-weight: 950;
  letter-spacing: 3px;
  margin-bottom: 12px;
}
.invite-hero p {
  color: rgba(255,255,255,0.78);
  font-size: 12px;
  line-height: 1.6;
}
.invite-card {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 16px;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.05);
  margin-bottom: 12px;
}
.invite-card-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 14px;
}
.invite-card-head span {
  display: block;
  color: #64748b;
  font-size: 12px;
  font-weight: 800;
  margin-bottom: 3px;
}
.invite-card-head strong {
  display: block;
  color: var(--tech-text);
  font-size: 15px;
  font-weight: 950;
}
.qr-premium {
  width: 196px;
  height: 196px;
  margin: 0 auto 14px;
  padding: 12px;
  border-radius: 8px;
  background: linear-gradient(135deg, #eff6ff, #f0fdfa);
  border: 1px solid #dbeafe;
}
.qr-premium img {
  width: 100%;
  height: 100%;
  display: block;
  border-radius: 6px;
  background: #fff;
}
.invite-link-box {
  padding: 12px;
  border-radius: 8px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  color: #334155;
  font-size: 12px;
  line-height: 1.5;
  word-break: break-all;
  margin-bottom: 12px;
}
.invite-link-box input {
  display: block;
  width: 100%;
  padding: 0;
  border: 0;
  outline: 0;
  background: transparent;
  color: #334155;
  font: inherit;
  font-size: 12px;
  line-height: 1.5;
  -webkit-user-select: text;
  user-select: text;
}
.invite-copy { width: 100%; }
.invite-stats {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  margin-bottom: 12px;
}
.invite-stats div {
  padding: 14px;
  border-radius: 8px;
  background: #fff;
  border: 1px solid #e2e8f0;
}
.invite-stats span {
  display: block;
  color: #64748b;
  font-size: 12px;
  font-weight: 800;
  margin-bottom: 4px;
}
.invite-stats strong {
  display: block;
  color: var(--tech-text);
  font-size: 20px;
  font-weight: 950;
  overflow-wrap: anywhere;
}
.invite-stats small,
.team-summary small {
  color: #94a3b8;
  font-size: 10px;
  font-weight: 800;
}
.reward-rules {
  margin-bottom: 12px;
  padding: 16px;
  border-radius: 8px;
  color: #fff;
  background: #172554;
}
.reward-rules .invite-card-head span { color: #93c5fd; }
.reward-rules .invite-card-head strong { color: #fff; }
.reward-rules .invite-card-head b {
  padding: 5px 8px;
  border-radius: 999px;
  color: #fef3c7;
  background: rgba(245, 158, 11, 0.18);
  font-size: 10px;
}
.reward-tier-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 6px;
}
.reward-tier-grid div {
  min-width: 0;
  padding: 10px 4px;
  border: 1px solid rgba(255,255,255,0.12);
  border-radius: 7px;
  background: rgba(255,255,255,0.07);
  text-align: center;
}
.reward-tier-grid span {
  display: block;
  color: #bfdbfe;
  font-size: 10px;
  margin-bottom: 3px;
}
.reward-tier-grid strong {
  color: #fff;
  font-size: 15px;
}
.invite-parent {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 14px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #fff;
}
.invite-parent span {
  color: #64748b;
  font-size: 12px;
  font-weight: 800;
}
.invite-parent strong {
  color: var(--tech-text);
  font-size: 16px;
  font-weight: 950;
}
.team-hero {
  padding: 20px;
  border-radius: 8px;
  background: #172554;
  color: #fff;
  margin-bottom: 12px;
  box-shadow: 0 14px 30px rgba(23, 37, 84, 0.2);
}
.team-hero span {
  color: #93c5fd;
  font-size: 11px;
  font-weight: 800;
}
.team-hero strong {
  display: block;
  margin: 3px 0 7px;
  font-size: 38px;
  line-height: 1;
  font-weight: 950;
}
.team-hero p {
  color: rgba(255,255,255,0.72);
  font-size: 11px;
  line-height: 1.55;
}
.team-summary {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 8px;
  margin-bottom: 16px;
}
.team-summary > div {
  min-width: 0;
  padding: 13px 8px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #fff;
  text-align: center;
}
.team-summary span {
  display: block;
  color: #64748b;
  font-size: 10px;
  font-weight: 800;
  margin-bottom: 4px;
}
.team-summary strong {
  display: block;
  color: var(--tech-text);
  font-size: 20px;
  font-weight: 950;
}
.team-section-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin: 0 2px 10px;
}
.team-section-head span {
  display: block;
  color: #94a3b8;
  font-size: 10px;
  font-weight: 800;
}
.team-section-head strong {
  display: block;
  margin-top: 2px;
  color: #1e293b;
  font-size: 14px;
}
.team-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.team-member {
  padding: 14px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #fff;
  box-shadow: 0 6px 18px rgba(15, 23, 42, 0.04);
}
.team-member-head {
  display: flex;
  align-items: center;
  gap: 11px;
}
.team-avatar {
  width: 42px;
  height: 42px;
  border-radius: 50%;
  overflow: hidden;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  background: #2563eb;
  font-size: 12px;
  font-weight: 900;
}
.team-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.team-identity {
  min-width: 0;
  flex: 1;
}
.team-identity strong {
  display: block;
  color: var(--tech-text);
  font-size: 13px;
  font-weight: 900;
  overflow-wrap: anywhere;
}
.team-identity span {
  display: block;
  margin-top: 4px;
  color: #64748b;
  font-family: monospace;
  font-size: 11px;
}
.team-level {
  flex-shrink: 0;
  padding: 5px 8px;
  border-radius: 999px;
  color: #4338ca;
  background: #e0e7ff;
  font-size: 10px;
  font-weight: 900;
}
.level-progress {
  margin-bottom: 20px;
  padding: 15px;
  border: 1px solid #dbeafe;
  border-radius: 8px;
  background: #f8fbff;
}
.level-progress-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}
.level-progress-head span {
  color: #64748b;
  font-size: 12px;
  font-weight: 750;
}
.level-progress-head strong {
  color: #1d4ed8;
  font-size: 18px;
  font-weight: 950;
}
.level-progress-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
}
.level-progress-grid > div {
  min-width: 0;
  padding: 10px;
  border-radius: 6px;
  background: #fff;
  border: 1px solid #e5e7eb;
}
.level-progress-grid span {
  display: block;
  color: #94a3b8;
  font-size: 10px;
  font-weight: 750;
}
.level-progress-grid strong {
  display: block;
  margin-top: 4px;
  color: var(--tech-text);
  font-size: 12px;
  font-weight: 900;
  overflow-wrap: anywhere;
}
.level-progress p {
  margin: 11px 0 0;
  color: #64748b;
  font-size: 11px;
  line-height: 1.55;
}
.earning-preview {
  display: grid;
  grid-template-columns: 1fr auto 1fr auto 1fr;
  align-items: center;
  gap: 7px;
  margin-bottom: 14px;
  padding: 13px;
  border-radius: 8px;
  border: 1px solid #ccfbf1;
  background: #f0fdfa;
}
.earning-preview > div {
  min-width: 0;
}
.earning-preview > b {
  color: #0f766e;
  font-size: 14px;
}
.earning-preview span {
  display: block;
  color: #64748b;
  font-size: 9px;
  font-weight: 750;
}
.earning-preview strong {
  display: block;
  margin-top: 3px;
  color: var(--tech-text);
  font-size: 11px;
  font-weight: 900;
  overflow-wrap: anywhere;
}
.earning-preview-result {
  grid-column: 1 / -1;
  margin-top: 4px;
  padding-top: 10px;
  border-top: 1px solid #ccfbf1;
  text-align: right;
}
.earning-preview-result strong {
  color: #059669;
  font-size: 17px;
}
.slide-enter-active, .slide-leave-active { transition: transform 0.35s cubic-bezier(0.16, 1, 0.3, 1); }
.slide-enter, .slide-leave-to { transform: translateX(100%); }
@media (max-width: 420px) {
  .drawer-panel { width: 100%; right: -100%; }
  .wallet-hero strong { font-size: 24px; }
}
</style>
